/**
 * Program 12.1 UX4 — Technical proposal sections cockpit.
 */
import React from 'react';
import { Card, Checkbox, List, Progress, Space, Tag, Typography } from 'antd';
import {
  normalizeSections,
  seedTechnicalSections,
  readinessTagColor,
  buildProposalReadiness,
} from './estimateProposalSubmissionReadiness';

const { Text, Title } = Typography;

export type EpcTechnicalProposalCockpitProps = {
  proposal: Record<string, unknown>;
  editable?: boolean;
  onToggleSection?: (key: string, complete: boolean) => void;
  pending?: boolean;
};

export const EpcTechnicalProposalCockpit: React.FC<EpcTechnicalProposalCockpitProps> = ({
  proposal,
  editable = true,
  onToggleSection,
  pending = false,
}) => {
  const tech = normalizeSections(proposal.technical_sections_json ?? proposal.technicalSectionsJson);
  const rows = tech.length ? tech : seedTechnicalSections().map((s) => ({ key: s.key, complete: false }));
  const complete = rows.filter((r) => r.complete).length;
  const pct = rows.length ? Math.round((complete / rows.length) * 100) : 0;
  const readiness = buildProposalReadiness(proposal);

  return (
    <Card size="small" title="TECHNICAL PROPOSAL">
      <Space wrap style={{ marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          Rev {String(proposal.revision || 1)}
        </Title>
        <Tag color={readinessTagColor(readiness.overall)}>{readiness.overall}</Tag>
        <Text type="secondary">
          {complete}/{rows.length} sections complete
        </Text>
      </Space>
      <Progress percent={pct} style={{ marginBottom: 12 }} />
      <List
        size="small"
        dataSource={rows}
        renderItem={(s) => (
          <List.Item>
            <Checkbox
              checked={s.complete}
              disabled={!editable || pending}
              onChange={(e) => onToggleSection?.(s.key, e.target.checked)}
            >
              {s.key}
            </Checkbox>
            <Tag color={s.complete ? 'green' : 'default'}>{s.complete ? 'Complete' : 'Missing'}</Tag>
          </List.Item>
        )}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        Completion {pct}% — submission readiness may still block if mandatory sections missing.
      </Text>
    </Card>
  );
};

export default EpcTechnicalProposalCockpit;
